import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, prefersReducedMotion, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink
 * @property {import('slideshow').Slideshow} [slideshow]
 * @property {import('quick-add').QuickAddComponent} [quickAdd]
 * @property {HTMLElement} [cardGallery]
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  /** @type {HTMLImageElement | null} */
  #fadeMask = null;

  /**
   * While flat view is on, hover swaps the card's flat image back to the on-model
   * image it replaced. Holds the flat source so pointerleave can restore it.
   * @type {{ image: HTMLImageElement, src: string, srcset: string } | null}
   */
  #flatHoverState = null;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.addEventListener(
      ThemeEvents.variantUpdate,
      /** @type {EventListener} */ (this.#handleVariantUpdate),
    );
    this.addEventListener(
      ThemeEvents.variantSelected,
      /** @type {EventListener} */ (this.#handleVariantSelected),
    );
    this.addEventListener('click', this.navigateToProduct);
    window.addEventListener('pageshow', this.#handlePageShow);

    // Preload the next image on the slideshow to avoid white flashes on hover/carousel
    setTimeout(() => {
      const slideshow = this.refs.slideshow;
      if (slideshow?.slides && slideshow.slides.length > 1) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    window.removeEventListener('pageshow', this.#handlePageShow);
    // A card can be disconnected mid-hover (grid re-render, morph reorder) with
    // no pointerleave to follow, so put the flat image back before the teardown.
    this.#restoreFlatImage(false);
    this.#removeFadeMask();
  }

  /**
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;
    this.#removeFadeMask();
    const { slideshow } = this.refs;
    if (!slideshow?.slides?.length) return;
    this.resetVariant.cancel();
    const baseIndex = this.#baseSlideIndex();
    slideshow.select(baseIndex, undefined, { animate: false });
    slideshow.alignToSlide(baseIndex);
  };

  /** Preloads the next preview image in the slideshow. */
  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute('loading');
  }

  /** Removes the crossfade mask overlay, if present. */
  #removeFadeMask() {
    const mask = this.#fadeMask;
    this.#fadeMask = null;
    mask?.remove();
  }

  /**
   * Moves the card carousel to `targetIndex` with a crossfade instead of the
   * default horizontal slide.
   *
   * `select()` would scroll, so instead we overlay a clone of the outgoing image,
   * jump the carousel underneath it, then fade the clone out. The destination
   * slide is pinned opaque throughout — otherwise base.css fades it in from 0 as
   * the mask fades out and the composite dips, showing the gray background.
   *
   * @param {number} targetIndex - The slide index to move to.
   */
  #crossfadeToSlide(targetIndex) {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const slides = slideshow.slides || [];
    if (slides.length < 2) return;

    let index = targetIndex;
    if (index < 0) index = slides.length - 1;
    else if (index > slides.length - 1) index = 0;

    if (index === slideshow.current) return;

    const currentImg = slides[slideshow.current]?.querySelector('img.product-media__image');
    const container = slideshow.querySelector('slideshow-container');
    const destSlide = slides[index];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Make sure the destination image is eligible to paint under the mask.
    destSlide?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');

    // Fallback to an instant (unmasked) select when a crossfade isn't possible.
    if (
      reduceMotion ||
      !(currentImg instanceof HTMLImageElement) ||
      !(container instanceof HTMLElement)
    ) {
      slideshow.select(index, undefined, { animate: false });
      return;
    }

    const mask = this.#addFadeMask(currentImg, container);

    // Pin the destination slide opaque (no fade-in from 0) so the mask fade-out
    // is a clean crossfade with no mid-transition dip. Restored on cleanup so
    // normal aria-hidden opacity behaviour (e.g. swatch preview) resumes.
    if (destSlide instanceof HTMLElement) {
      destSlide.style.transition = 'none';
      destSlide.style.opacity = '1';
    }

    // Jump to the destination slide hidden beneath the mask, then fade out.
    slideshow.select(index, undefined, { animate: false });

    this.#fadeOutMask(mask, () => {
      if (destSlide instanceof HTMLElement) {
        destSlide.style.transition = '';
        destSlide.style.opacity = '';
      }
    });
  }

  /**
   * Overlays an opaque clone of `sourceImg` on the slideshow so what is beneath
   * it can change without the swap being visible.
   *
   * @param {HTMLImageElement} sourceImg - The image currently on screen.
   * @param {HTMLElement} container - The slideshow container to overlay.
   * @returns {HTMLImageElement} The mask element.
   */
  #addFadeMask(sourceImg, container) {
    this.#removeFadeMask();

    const mask = /** @type {HTMLImageElement} */ (sourceImg.cloneNode(true));
    mask.classList.add('product-card__fade-mask');
    mask.removeAttribute('loading');
    mask.setAttribute('aria-hidden', 'true');
    const styles = getComputedStyle(sourceImg);
    mask.style.cssText =
      'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;opacity:1;z-index:1;transition:opacity 0.3s ease;';
    mask.style.objectFit = styles.objectFit || 'cover';
    mask.style.objectPosition = styles.objectPosition || 'center center';

    container.appendChild(mask);
    this.#fadeMask = mask;

    return mask;
  }

  /**
   * Fades a mask out to reveal what was swapped in beneath it, then removes it.
   * The timeout is a safety net for a `transitionend` that never fires.
   *
   * @param {HTMLImageElement} mask - The mask returned by `#addFadeMask`.
   * @param {() => void} [onCleanup] - Extra teardown once the mask is gone.
   */
  #fadeOutMask(mask, onCleanup) {
    requestAnimationFrame(() => {
      if (this.#fadeMask === mask) mask.style.opacity = '0';
    });

    const cleanup = () => {
      if (this.#fadeMask === mask) this.#fadeMask = null;
      mask.remove();
      onCleanup?.();
    };
    mask.addEventListener('transitionend', cleanup, { once: true });
    setTimeout(cleanup, 400);
  }

  /**
   * Resolves the card's base slide index: the selected variant's image if any,
   * otherwise the initial slide (falling back to the first slide).
   * @returns {number}
   */
  #baseSlideIndex() {
    const { slideshow } = this.refs;
    if (!slideshow) return 0;

    const slides = slideshow.slides || [];

    const variantMediaId = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    if (variantMediaId) {
      const variantIndex = slides.findIndex(
        (slide) => slide.getAttribute('slide-id') === variantMediaId,
      );
      if (variantIndex >= 0) return variantIndex;
    }

    const initialIndex = slideshow.initialSlide ? slides.indexOf(slideshow.initialSlide) : -1;
    return initialIndex >= 0 ? initialIndex : 0;
  }

  /**
   * Handles the variant selected event.
   * @param {import('@theme/events').VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);

    // Update quick-add with variant data if available
    const variant = /** @type {any} */ (event.detail.data).variant;
    if (variant) {
      this.refs.quickAdd?.updateForVariant(variant);
    }

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        }),
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(
      `product-price [ref='priceContainer']`,
    );

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const parsedUrl = new URL(anchorElement.href);
      parsedUrl.searchParams.delete('variant');
      const productUrl = parsedUrl.toString();
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /** Hide the variant images that are not for the selected variant. */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  #primaryInputSupportsHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  /**
   * Shows the second image on hover by advancing the actual card carousel to
   * the next slide with a crossfade. Driving the real slideshow (rather than a
   * detached overlay) keeps hover and the arrows on one shared index, so
   * clicking "next" continues forward instead of re-showing the previewed image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (!this.#primaryInputSupportsHover()) return;
    if (event.pointerType !== 'mouse') return;

    // Flat view (VBR-314) replaced the first slide's image with the _FLAT one,
    // so the on-model image it hid — not the second slide — is what hover shows.
    if (this.#previewOnModelImage()) {
      this.resetVariant.cancel();
      return;
    }

    const { slideshow } = this.refs;
    if (!slideshow || !slideshow.slides || slideshow.slides.length < 2) return;

    this.resetVariant.cancel();
    this.#crossfadeToSlide(slideshow.nextIndex);
  }

  /**
   * Crossfades the card carousel back to the base image (selected variant image
   * or first slide) once the pointer leaves the card.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (!this.#primaryInputSupportsHover()) return;
    if (event.pointerType !== 'mouse') return;

    this.resetVariant.cancel();

    const { slideshow } = this.refs;
    const baseIndex = this.#baseSlideIndex();
    const needsSlideReset = Boolean(slideshow?.slides?.length) && slideshow.current !== baseIndex;

    this.#restoreFlatImage(!needsSlideReset);

    if (needsSlideReset) this.#crossfadeToSlide(baseIndex);
  }

  /**
   * Crossfades a flat-view card back to the on-model image the flat image
   * replaced. `data-flat-original-src` is set by `setCardFlatImage`
   * (assets/flat-view.js) and is only present while a flat image is displayed,
   * so it doubles as the check for "this card is currently in flat view".
   *
   * @returns {boolean} True when the card is in flat view and hover was handled.
   */
  #previewOnModelImage() {
    if (this.#flatHoverState) return true;

    const image = this.querySelector('img.product-media__image[data-flat-original-src]');
    if (!(image instanceof HTMLImageElement)) return false;

    const { slideshow } = this.refs;
    const flatSlide = image.closest('slideshow-slide');
    const currentSlide = slideshow?.slides?.[slideshow.current];
    if (flatSlide && currentSlide && flatSlide !== currentSlide) return false;

    this.#flatHoverState = {
      image,
      src: image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
    };
    this.#crossfadeImageSource(
      image,
      image.dataset.flatOriginalSrc || '',
      image.dataset.flatOriginalSrcset || '',
    );

    return true;
  }

  /**
   * Restores the flat image after a hover preview.
   *
   * Skips the restore when the card left flat view mid-hover (the toggle was
   * switched off, or a swatch change re-rendered the gallery) — `flat-view.js`
   * clears `data-flat-original-src` in both cases, and re-applying the stale
   * flat source would fight it.
   *
   * @param {boolean} [animate] - False to swap back instantly (card teardown).
   * @returns {boolean} True when a flat hover preview was active.
   */
  #restoreFlatImage(animate = true) {
    const state = this.#flatHoverState;
    if (!state) return false;

    this.#flatHoverState = null;

    const { image, src, srcset } = state;
    if (image.dataset.flatOriginalSrc == null) return true;
    if (!animate || !image.isConnected) {
      this.#setImageSource(image, src, srcset);
      return true;
    }

    this.#crossfadeImageSource(image, src, srcset);

    return true;
  }

  /**
   * @param {HTMLImageElement} image
   * @param {string} src
   * @param {string} srcset - Removed from the image when empty.
   */
  #setImageSource(image, src, srcset) {
    image.setAttribute('src', src);
    if (srcset) image.setAttribute('srcset', srcset);
    else image.removeAttribute('srcset');
  }

  /**
   * Swaps an image's source behind a fade mask, so the change reads as a
   * crossfade rather than a hard cut. The mask is held until the new source is
   * decodable to avoid flashing an empty frame.
   *
   * @param {HTMLImageElement} image - The image to swap.
   * @param {string} src - The source to swap in.
   * @param {string} srcset - The srcset to swap in (removed when empty).
   */
  #crossfadeImageSource(image, src, srcset) {
    const container = this.refs.slideshow?.querySelector('slideshow-container');

    if (prefersReducedMotion() || !(container instanceof HTMLElement)) {
      this.#setImageSource(image, src, srcset);
      return;
    }

    const mask = this.#addFadeMask(image, container);
    this.#setImageSource(image, src, srcset);

    // Hold the mask until the new source can paint, but never longer than the
    // timeout — a decode that stalls must not leave the old image frozen on top.
    let faded = false;
    const fadeOut = () => {
      if (faded) return;
      faded = true;
      clearTimeout(stallTimeout);
      this.#fadeOutMask(mask);
    };
    const stallTimeout = setTimeout(fadeOut, 1000);
    image.decode().then(fadeOut, fadeOut);
  }

  /** Resets the image to the variant image. */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts clicks on the card anchor to push an intermediate history state capturing the
   * current page and this card, so going back returns to the same page with the card in view.
   *
   * Skips navigation when the click landed on another interactive element (e.g. variant picker).
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /** Resets the variant. */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /** Updates the card URL when a variant is selected. */
  #handleCardVariantUrlUpdate() {
    this.pendingVariantId = null;
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput =
      event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
